import React, {
  ChangeEvent,
  FocusEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import TextField from '@mui/material/TextField';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import CalendarIcon from 'core/icons/calendar';
import Popover from 'core/components/Popover';
import {
  Matcher,
  dateMatchModifiers,
} from 'react-day-picker';
import {
  isEmptyString,
  notEmptyString,
} from 'utils/misc';
import Calendar from 'core/components/DateTimePicker/base/Calendar';
import {
  parseFlexibleDate,
} from 'utils/date';
import DateTextField from 'core/components/DateTimePicker/inputs/DateTextField';
import {
  PickerProps,
  Translations,
} from 'core/components/DateTimePicker/types';

type Props = Omit<PickerProps, 'translations'> & {
  value?: Date | string | Dayjs | null;
  defaultValue?: Date | string | Dayjs | null;
  onChange?: (date: Date | undefined) => void;
  translations?: Omit<Translations, 'range'>;
  inputFormat?: string;
  placeholder?: string;
  onBlur?: () => void;
  inputProps?: object;
  inputRef?: React.RefObject<HTMLInputElement>;
  disablePast?: boolean;
  disableFuture?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  popoverPaperClassName?: string;
  popoverContentClassName?: string;
  calendarClassName?: string;
};

const defaultInputFormat = 'MM/DD/YYYY';

function toDayjsValue(value?: Date | string | Dayjs | null) {
  return isNil(value) || isEmptyString(value)
    ? null
    : dayjs(value);
}

function formatInputValue(value: Dayjs | null, inputFormat: string) {
  return value?.isValid() ? value.format(inputFormat) : '';
}

function parseInputValue(value: string, inputFormat: string) {
  if (isEmptyString(value)) {
    return null;
  }

  const directParse = dayjs(value, inputFormat, true);
  if (directParse.isValid()) {
    return directParse;
  }

  return parseFlexibleDate(value);
}

export default function DatePicker({
  value,
  defaultValue,
  onChange,
  onBlur,
  className,
  disabled = false,
  inputFormat = defaultInputFormat,
  placeholder = 'Select date',
  inputProps,
  inputRef,
  disabledDays,
  disablePast,
  disableFuture,
  minDate,
  maxDate,
  popoverPaperClassName,
  popoverContentClassName,
  calendarClassName,
  enableYearNavigation,
  disableNavigation,
  numberOfMonths,
  fromYear,
  toYear,
  fromMonth,
  toMonth,
  fromDate,
  toDate,
  translations,
  align = 'center',
  id = 'single-date-picker',
  ...props
}: Props) {
  const pickerValue = useMemo(
    () => toDayjsValue(value ?? defaultValue),
    [value, defaultValue],
  );
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [month, setMonth] = useState<Date | undefined>(pickerValue?.toDate());
  const [inputValue, setInputValue] = useState(formatInputValue(pickerValue, inputFormat));
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const useMaskedDateInput = useMemo(
    () => inputFormat.toUpperCase() === defaultInputFormat,
    [inputFormat],
  );

  useEffect(() => {
    setInputValue(formatInputValue(pickerValue, inputFormat));
    if (!anchorEl) {
      setMonth(pickerValue?.toDate());
    }
  }, [pickerValue, inputFormat, anchorEl]);

  const closePopover = () => {
    setAnchorEl(null);
  };

  const commitDate = (nextValue?: Dayjs | null) => {
    if (isNil(nextValue) || !nextValue.isValid() || isDateDisabled(nextValue.toDate())) {
      return;
    }

    const formattedNextValue = formatInputValue(nextValue, inputFormat);

    if (pickerValue?.isValid() && pickerValue.isSame(nextValue, 'day')) {
      setInputValue(formattedNextValue);
      onBlur?.();
      return;
    }

    onChange?.(nextValue.toDate());
    onBlur?.();
    setInputValue(formattedNextValue);
  };

  const disabledMatchers = useMemo(() => {
    const matchers: Matcher[] = [];

    if (!isNil(disabledDays)) {
      if (Array.isArray(disabledDays)) {
        matchers.push(...disabledDays);
      } else {
        matchers.push(disabledDays);
      }
    }

    if (disablePast) {
      matchers.push({ before: dayjs().startOf('day').toDate() });
    }

    if (disableFuture) {
      matchers.push({ after: dayjs().endOf('day').toDate() });
    }

    if (!isNil(minDate)) {
      matchers.push({ before: dayjs(minDate).startOf('day').toDate() });
    }

    if (!isNil(maxDate)) {
      matchers.push({ after: dayjs(maxDate).endOf('day').toDate() });
    }

    return matchers.length > 0 ? matchers : undefined;
  }, [disabledDays, disablePast, disableFuture, minDate, maxDate]);

  const isDateDisabled = (date: Date) => (
    !isNil(disabledMatchers) ? dateMatchModifiers(date, disabledMatchers) : false
  );

  const calendarMinMonth = fromDate
    ?? minDate?.toDate()
    ?? (disablePast ? dayjs().startOf('day').toDate() : undefined)
    ?? fromMonth
    ?? (fromYear ? new Date(fromYear, 0, 1) : undefined);

  const calendarMaxMonth = toDate
    ?? maxDate?.toDate()
    ?? (disableFuture ? dayjs().endOf('day').toDate() : undefined)
    ?? toMonth
    ?? (toYear ? new Date(toYear, 11, 31) : undefined);
  const popoverHorizontal = align === 'start'
    ? 'left'
    : align === 'end'
      ? 'right'
      : 'center';

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    const currentFormatted = formatInputValue(pickerValue, inputFormat);
    const nextValue = event.target.value;

    if (isEmptyString(nextValue)) {
      onChange?.(undefined);
      onBlur?.();
      setInputValue('');
      return;
    }

    if (notEmptyString(nextValue) && nextValue !== currentFormatted) {
      const parsed = parseInputValue(nextValue, inputFormat);

      if (!isNil(parsed) && parsed.isValid() && !isDateDisabled(parsed.toDate())) {
        commitDate(parsed);
      } else {
        setInputValue(currentFormatted);
      }
    } else {
      setInputValue(currentFormatted);
    }

    onBlur?.();
  };

  const handleMaskedDateChange = (dateValue: string | Date | null | undefined) => {
    if (isNil(dateValue)) {
      onChange?.(undefined);
      setInputValue('');
      return;
    }

    const nextDate = dateValue instanceof Date
      ? dayjs(dateValue)
      : parseInputValue(dateValue?.toString() ?? '', inputFormat);

    if (!isNil(nextDate) && nextDate.isValid() && !isDateDisabled(nextDate.toDate())) {
      commitDate(nextDate);
    }
  };

  const handleMonthSelect = (nextDate: Date | undefined) => {
    if (!isNil(nextDate) && !isDateDisabled(nextDate)) {
      commitDate(dayjs(nextDate));
      closePopover();
    }
  };

  return (
    <>
      <div
        ref={inputContainerRef}
        className={clsx('relative min-w-0 w-full', className)}
      >
        {useMaskedDateInput ? (
          <DateTextField
            id={id}
            inputRef={inputRef as React.RefObject<HTMLInputElement | null>}
            value={pickerValue?.toDate() ?? null}
            onChange={handleMaskedDateChange}
            onBlur={() => {
              onBlur?.();
            }}
            disabled={disabled}
            placeholder={inputFormat.toLowerCase()}
            className={clsx(
              className,
              'populate-input populate-date-picker populate-date-picker-with-icon !m-0 !w-full !text-xs',
              { '!border-red-400': props.hasError },
            )}
            inputProps={inputProps as any}
          />
        ) : (
          <TextField
            id={id}
            inputRef={inputRef}
            className={clsx(
              className,
              'populate-input populate-date-picker populate-date-picker-with-icon !m-0 !w-full !text-xs',
              { '!border-red-400': props.hasError },
            )}
            value={inputValue}
            disabled={disabled}
            placeholder={inputFormat.toLowerCase()}
            onChange={handleTextChange}
            onBlur={handleInputBlur}
            slotProps={{
              htmlInput: inputProps as any,
            }}
          />
        )}
        <button
          type="button"
          className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center text-neutral-700"
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={() => {
            setAnchorEl(inputContainerRef.current);
          }}
          aria-label="Open calendar"
          disabled={disabled}
        >
          <CalendarIcon className="!w-5 !h-5 !text-gray-700" />
        </button>
      </div>
      <Popover
        id={id}
        anchorEl={anchorEl}
        onClose={closePopover}
        paperClasses={clsx(
          '!w-fit !min-w-0 !max-w-none !overflow-visible !shadow-[0px_0px_38px_-15px_rgba(0,_0,_0,_0.35)]',
          popoverPaperClassName,
        )}
        anchorOrigin={{
          vertical: (anchorEl?.offsetHeight ?? 0) + 8,
          horizontal: popoverHorizontal,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: popoverHorizontal,
        }}
      >
        <div className={clsx('inline-flex w-fit border border-gray-100 rounded-md bg-white', popoverContentClassName)}>
          <Calendar
            mode="single"
            compact
            classNames={{
              selected: 'day_single_selected',
              today: 'day_single_today',
            }}
            month={month}
            onMonthChange={setMonth}
            selected={pickerValue?.toDate()}
            onSelect={handleMonthSelect}
            disabled={disabledMatchers}
            enableYearNavigation={enableYearNavigation}
            disableNavigation={disableNavigation}
            numberOfMonths={numberOfMonths}
            fromYear={fromYear}
            toYear={toYear}
            fromMonth={calendarMinMonth}
            toMonth={calendarMaxMonth}
            fromDate={calendarMinMonth}
            toDate={calendarMaxMonth}
            initialFocus
            className={clsx('w-fit shrink-0', calendarClassName)}
            formatters={{
              formatWeekdayName: (day: Date) => dayjs(day).format('ddd').substring(0, 1),
            }}
            {...props}
          />
        </div>
      </Popover>
    </>
  );
}
